import { cn } from '~/lib/utils'

/*
 * Truchet tile field — generative drafting-style linework for marketing
 * backgrounds. Each square cell holds two quarter-circle arcs in one of two
 * orientations ("Smith tiles"); arcs always meet at edge midpoints, so any
 * random arrangement connects seamlessly, including across the repeating
 * pattern block. Strokes inherit `currentColor` like the other marketing
 * art, so call sites set colour and opacity via className.
 *
 * The field is deterministic: the same `seed` always draws the same block,
 * which keeps prerendered marketing pages identical across builds.
 */

type TruchetFieldProps = {
  /** Tiles per side of the repeating pattern block. */
  block?: number
  /** Edge length of one square tile, in px. */
  cell?: number
  className?: string
  /** Number or string (e.g. a section slug) — same seed, same pattern. */
  seed?: number | string
  strokeWidth?: number
  /**
   * 'arcs' is the uniform grid; 'multiscale' recursively subdivides some
   * tiles into 2×2, giving a denser, more intricate field for feature
   * moments. The connections still hold at every scale.
   */
  variant?: 'arcs' | 'multiscale'
}

// biome-ignore-start lint/suspicious/noBitwiseOperators: FNV-1a and mulberry32 are 32-bit bit-twiddling by definition
/** FNV-1a, folds a string seed down to a 32-bit int. */
const hashSeed = (seed: number | string): number => {
  if (typeof seed === 'number') {
    return Math.floor(seed)
  }
  let hash = 0x81_1c_9d_c5
  for (let index = 0; index < seed.length; index++) {
    hash ^= seed.charCodeAt(index)
    hash = Math.imul(hash, 0x01_00_01_93)
  }
  return hash
}

/** mulberry32, a tiny deterministic PRNG. */
const mulberry32 = (seed: number) => {
  let state = seed
  return () => {
    state = (state + 0x6d_2b_79_f5) | 0
    let t = Math.imul(state ^ (state >>> 15), 1 | state)
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t)
    return ((t ^ (t >>> 14)) >>> 0) / 4_294_967_296
  }
}
// biome-ignore-end lint/suspicious/noBitwiseOperators: FNV-1a and mulberry32 are 32-bit bit-twiddling by definition

/** Pushes the two quarter-arcs of one tile onto `parts`. */
const tileArcs = (parts: string[], x: number, y: number, size: number, flipped: boolean) => {
  const r = size / 2
  if (flipped) {
    // Arcs hugging the top-right and bottom-left corners.
    parts.push(
      `M${x + size} ${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}`,
      `M${x} ${y + r}A${r} ${r} 0 0 1 ${x + r} ${y + size}`
    )
  } else {
    // Arcs hugging the top-left and bottom-right corners.
    parts.push(
      `M${x + r} ${y}A${r} ${r} 0 0 1 ${x} ${y + r}`,
      `M${x + r} ${y + size}A${r} ${r} 0 0 1 ${x + size} ${y + r}`
    )
  }
}

/** One `d` string holding the arcs of every tile in the block. */
const blockPath = (
  seed: number | string,
  block: number,
  cell: number,
  variant: 'arcs' | 'multiscale'
): string => {
  const next = mulberry32(hashSeed(seed))
  const parts: string[] = []
  const drawTile = (x: number, y: number, size: number, depth: number) => {
    if (variant === 'multiscale' && depth < 2 && size > 18 && next() < 0.4) {
      const half = size / 2
      drawTile(x, y, half, depth + 1)
      drawTile(x + half, y, half, depth + 1)
      drawTile(x, y + half, half, depth + 1)
      drawTile(x + half, y + half, half, depth + 1)
      return
    }
    tileArcs(parts, x, y, size, next() < 0.5)
  }
  for (let row = 0; row < block; row++) {
    for (let col = 0; col < block; col++) {
      drawTile(col * cell, row * cell, cell, 0)
    }
  }
  return parts.join('')
}

export const TruchetField = ({
  block = 8,
  cell = 44,
  className,
  seed = 1,
  strokeWidth = 1,
  variant = 'arcs',
}: TruchetFieldProps) => {
  // `useId()` depends on the surrounding React tree. Marketing pages are
  // prerendered through a smaller provider tree than the hydrated client, so
  // those ids diverged and every patterned section logged a hydration
  // mismatch. The artwork is already seed-deterministic; its SVG id should be
  // too. Seeds are unique per page by component contract.
  const signedHash = hashSeed(seed)
  const unsignedHash = signedHash < 0 ? signedHash + 4_294_967_296 : signedHash
  const patternId = `truchet-${unsignedHash.toString(36)}-${block}-${cell}-${variant}`
  const size = block * cell
  return (
    <svg
      aria-hidden="true"
      className={cn('pointer-events-none', className)}
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern height={size} id={patternId} patternUnits="userSpaceOnUse" width={size}>
          <path
            d={blockPath(seed, block, cell, variant)}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
          />
        </pattern>
      </defs>
      <rect fill={`url(#${patternId})`} height="100%" width="100%" />
    </svg>
  )
}
